import React from 'react';
import PropTypes from 'prop-types';
import {Box, Chip, Divider, Grid, Paper, Typography} from '@mui/material';

/**
 * The high level read of an owner: what it is, then how much of the platform it touches.
 *
 * The counts are the same numbers the tabs below are built from, surfaced up front so the
 * shape of an owner is readable without opening a single tab.
 */

const Stat = ({label, value, loading}) => (
  <Paper variant='outlined' sx={{p: 1.5, textAlign: 'center', height: '100%'}}>
    <Typography variant='h5'>{loading ? '—' : value}</Typography>
    <Typography variant='caption' color='textSecondary'>{label}</Typography>
  </Paper>
);

const facts = (owner, businessName) => [
  ['ID', owner.id],
  ['Business', businessName || owner.business_id || '—'],
  ['Time zone', owner.timezone || '—'],
  ['Created', owner.created_datetime || '—'],
  ['Updated by', owner.updated_by || '—'],
];

const OwnerSummary = ({owner, businessName, stats, loading}) => (
  <Box sx={{mb: 2}}>
    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2}}>
      {owner.multi_site === 1 && <Chip size='small' color='primary' label='Multi-site'/>}
      {owner.disabled ? <Chip size='small' color='warning' label='Disabled'/> : null}
      {owner.timezone && <Chip size='small' variant='outlined' label={owner.timezone}/>}
    </Box>

    <Grid container spacing={1.5} sx={{mb: 2}}>
      {stats.map((stat) => (
        <Grid item xs={6} sm={3} key={stat.label}>
          <Stat label={stat.label} value={stat.value} loading={loading}/>
        </Grid>
      ))}
    </Grid>

    <Grid container spacing={1}>
      {facts(owner, businessName).map(([label, value]) => (
        <Grid item xs={12} sm={6} md={4} key={label} sx={{display: 'flex', gap: 1}}>
          <Typography variant='body2' color='textSecondary' sx={{minWidth: 92}}>
            {label}
          </Typography>
          <Typography variant='body2' noWrap title={String(value)}>{String(value)}</Typography>
        </Grid>
      ))}
    </Grid>

    <Divider sx={{mt: 2}}/>
  </Box>
);

OwnerSummary.propTypes = {
  owner: PropTypes.object.isRequired,
  businessName: PropTypes.string,
  stats: PropTypes.array.isRequired,
  loading: PropTypes.bool,
};

export default OwnerSummary;
